import { Permission } from '@qualicloud/contracts';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { ProtectedRoute } from '@/components/protected-route';
import { hasSession } from '@/lib/api';
import { ApprovalsPage } from '@/pages/approvals.page';
import { AuditTrailPage } from '@/pages/audit-trail.page';
import { CapaActionDetailPage } from '@/pages/capa-action-detail.page';
import { CapaActionsPage } from '@/pages/capa-actions.page';
import { ChangePasswordPage } from '@/pages/change-password.page';
import { CircularDetailPage } from '@/pages/circular-detail.page';
import { CircularFormPage } from '@/pages/circular-form.page';
import { CircularsPage } from '@/pages/circulars.page';
import { DashboardPage } from '@/pages/dashboard.page';
import { DocumentAdminPage } from '@/pages/document-admin.page';
import { DocumentDetailPage } from '@/pages/document-detail.page';
import { DocumentFormPage } from '@/pages/document-form.page';
import { MeetingTypesPage } from '@/pages/meeting-types.page';
import { RequestTypesPage } from '@/pages/request-types.page';
import { DocumentTypesPage } from '@/pages/document-types.page';
import { LoginPage } from '@/pages/login.page';
import { MasterListPage } from '@/pages/master-list.page';
import { MeetingDetailPage } from '@/pages/meeting-detail.page';
import { MeetingFormPage } from '@/pages/meeting-form.page';
import { MeetingsPage } from '@/pages/meetings.page';
import { NonconformitiesPage } from '@/pages/nonconformities.page';
import { NonconformityDetailPage } from '@/pages/nonconformity-detail.page';
import { NonconformityFormPage } from '@/pages/nonconformity-form.page';
import { NotFoundPage } from '@/pages/not-found.page';
import { NotificationsPage } from '@/pages/notifications.page';
import { OrganizationPage } from '@/pages/organization.page';
import { ProfilePage } from '@/pages/profile.page';
import { SgqIndicatorsPage } from '@/pages/sgq-indicators.page';
import { UsersPage } from '@/pages/users.page';
import { AppShell } from '@/shell/app-shell';
import {
  asBoolean,
  asDate,
  asString,
  asStringArray,
  toPositiveInt,
  type AuditSearch,
  type CapaActionSearch,
  type ChangePasswordSearch,
  type CircularSearch,
  type DocumentAdminSearch,
  type LoginSearch,
  type MasterListSearch,
  type MeetingSearch,
  type NonconformitySearch,
  type SgqIndicatorsSearch,
} from './search-schemas';

/**
 * Árvore de rotas em CÓDIGO, não por convenção de arquivo.
 *
 * Motivo: rotas geradas por scanner de diretório dependem de um passo de
 * codegen no build, e num sistema validado cada artefato gerado é mais uma
 * coisa a justificar na qualificação de instalação. A árvore explícita é
 * revisável no código e não exige plugin no pipeline.
 *
 * Os caminhos são em PORTUGUÊS porque a URL é interface — o usuário lê, copia e
 * cola em chamado. O código continua em inglês (architecture.md §12).
 */

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

// ── Rotas públicas ───────────────────────────────────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: asString(search.redirect),
  }),
  beforeLoad: () => {
    // Sessão já ativa não vê a tela de entrada.
    if (hasSession()) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

// ── Casca autenticada ────────────────────────────────────────────────────────

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: () => (
    <ProtectedRoute>
      <DashboardPage />
    </ProtectedRoute>
  ),
});

const changePasswordRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/trocar-senha',
  validateSearch: (search: Record<string, unknown>): ChangePasswordSearch => ({
    expired: asBoolean(search.expired),
    username: asString(search.username),
  }),
  component: ChangePasswordPage,
});

const profileRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/perfil',
  component: () => (
    <ProtectedRoute>
      <ProfilePage />
    </ProtectedRoute>
  ),
});

// ── GED ──────────────────────────────────────────────────────────────────────

const masterListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/documentos',
  validateSearch: (search: Record<string, unknown>): MasterListSearch => ({
    page: toPositiveInt(search.page) ?? 1,
    pageSize: toPositiveInt(search.pageSize),
    documentTypeId: asString(search.documentTypeId),
    includeDescendantTypes: asBoolean(search.includeDescendantTypes),
    status: asStringArray(search.status),
    search: asString(search.search),
    expiringWithinDays: toPositiveInt(search.expiringWithinDays),
    onlyPendingMyApproval: asBoolean(search.onlyPendingMyApproval),
    onlyPendingMyAcknowledgement: asBoolean(search.onlyPendingMyAcknowledgement),
    sortBy: asString(search.sortBy),
    sortDir: search.sortDir === 'desc' ? 'desc' : 'asc',
  }),
  component: () => (
    <ProtectedRoute>
      <MasterListPage />
    </ProtectedRoute>
  ),
});

const newDocumentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/documentos/novo',
  component: () => (
    <ProtectedRoute permissions={[Permission.DOCUMENT_CREATE]}>
      <DocumentFormPage mode="create" />
    </ProtectedRoute>
  ),
});

/**
 * Administração de documentos — `ged/ged/admin` do legado (ged.md §14.3/§15.2).
 *
 * Segmento LITERAL, declarado ao lado de `/documentos/$documentId`: o roteador
 * dá precedência ao literal sobre o parâmetro, como já acontece com
 * `/documentos/novo`. Sem `document:read:all` o `<ProtectedRoute>` recusa — e o
 * servidor recusa de novo, porque esconder rota não é controle de acesso.
 */
const documentAdminRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/documentos/administracao',
  validateSearch: (search: Record<string, unknown>): DocumentAdminSearch => ({
    page: toPositiveInt(search.page) ?? 1,
    pageSize: toPositiveInt(search.pageSize),
    search: asString(search.search),
    documentTypeId: asString(search.documentTypeId),
    authorUserId: asString(search.authorUserId),
    status: asStringArray(search.status),
    createdFrom: asString(search.createdFrom),
    createdTo: asString(search.createdTo),
  }),
  component: () => (
    <ProtectedRoute permissions={[Permission.DOCUMENT_READ_ALL]}>
      <DocumentAdminPage />
    </ProtectedRoute>
  ),
});

const documentDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/documentos/$documentId',
  component: () => (
    <ProtectedRoute>
      <DocumentDetailPage />
    </ProtectedRoute>
  ),
});

const newVersionRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/documentos/$documentId/nova-versao',
  component: () => (
    <ProtectedRoute>
      <DocumentFormPage mode="new-version" />
    </ProtectedRoute>
  ),
});

const approvalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/aprovacoes',
  component: () => (
    <ProtectedRoute>
      <ApprovalsPage />
    </ProtectedRoute>
  ),
});

const documentTypesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tipos-documento',
  component: () => (
    <ProtectedRoute permissions={[Permission.DOCUMENT_TYPE_MANAGE]}>
      <DocumentTypesPage />
    </ProtectedRoute>
  ),
});

/*
 * Catálogos do SGQ. Ficam junto de `/tipos-documento` no menu porque são a mesma
 * natureza de cadastro, e é ali que o usuário já procura tabela de apoio.
 */
const meetingTypesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tipos-reuniao',
  component: () => (
    <ProtectedRoute permissions={[Permission.SGQ_TYPE_MANAGE]}>
      <MeetingTypesPage />
    </ProtectedRoute>
  ),
});

const requestTypesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tipos-solicitacao',
  component: () => (
    <ProtectedRoute permissions={[Permission.SGQ_TYPE_MANAGE]}>
      <RequestTypesPage />
    </ProtectedRoute>
  ),
});

// ── SGQ — Onda 2 ─────────────────────────────────────────────────────────────
//
// Os caminhos seguem a mesma convenção do GED: URL em PORTUGUÊS, porque o
// usuário lê, copia e cola em chamado. O segmento literal (`/nova`) vem
// declarado ANTES do parâmetro para deixar explícita a precedência que o
// roteador aplica — igual a `/documentos/novo` × `/documentos/$documentId`.

const nonconformitiesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/nao-conformidades',
  validateSearch: (search: Record<string, unknown>): NonconformitySearch => ({
    page: toPositiveInt(search.page) ?? 1,
    pageSize: toPositiveInt(search.pageSize),
    scope: asString(search.scope),
    status: asStringArray(search.status),
    type: asStringArray(search.type),
    source: asStringArray(search.source),
    departmentId: asString(search.departmentId),
    assigneeUserId: asString(search.assigneeUserId),
    search: asString(search.search),
    detectedFrom: asString(search.detectedFrom),
    detectedTo: asString(search.detectedTo),
    onlyRecurrent: asBoolean(search.onlyRecurrent),
    onlyOverdueCapa: asBoolean(search.onlyOverdueCapa),
    criticality: asStringArray(search.criticality),
    sortBy: asString(search.sortBy),
    sortDir: search.sortDir === 'asc' ? 'asc' : 'desc',
  }),
  component: () => (
    <ProtectedRoute>
      <NonconformitiesPage />
    </ProtectedRoute>
  ),
});

const newNonconformityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/nao-conformidades/nova',
  component: () => (
    <ProtectedRoute permissions={[Permission.NC_CREATE]}>
      <NonconformityFormPage />
    </ProtectedRoute>
  ),
});

const nonconformityDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/nao-conformidades/$nonconformityId',
  component: () => (
    <ProtectedRoute>
      <NonconformityDetailPage />
    </ProtectedRoute>
  ),
});

const meetingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/reunioes',
  validateSearch: (search: Record<string, unknown>): MeetingSearch => ({
    page: toPositiveInt(search.page) ?? 1,
    pageSize: toPositiveInt(search.pageSize),
    status: asStringArray(search.status),
    meetingTypeIds: asStringArray(search.meetingTypeIds),
    scope: asString(search.scope),
    nonconformityId: asString(search.nonconformityId),
    search: asString(search.search),
    startsFrom: asString(search.startsFrom),
    startsTo: asString(search.startsTo),
    includeCancelled: asBoolean(search.includeCancelled),
    sortBy: asString(search.sortBy),
    sortDir: search.sortDir === 'asc' ? 'asc' : 'desc',
    view: asString(search.view),
  }),
  component: () => (
    <ProtectedRoute>
      <MeetingsPage />
    </ProtectedRoute>
  ),
});

const newMeetingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/reunioes/nova',
  validateSearch: (search: Record<string, unknown>): MeetingSearch => ({
    nonconformityId: asString(search.nonconformityId),
  }),
  component: () => (
    <ProtectedRoute permissions={[Permission.MEETING_MANAGE]}>
      <MeetingFormPage />
    </ProtectedRoute>
  ),
});

const meetingDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/reunioes/$meetingId',
  component: () => (
    <ProtectedRoute>
      <MeetingDetailPage />
    </ProtectedRoute>
  ),
});

const capaActionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/acoes-corretivas',
  validateSearch: (search: Record<string, unknown>): CapaActionSearch => ({
    page: toPositiveInt(search.page) ?? 1,
    pageSize: toPositiveInt(search.pageSize),
    scope: asString(search.scope),
    status: asStringArray(search.status),
    nonconformityId: asString(search.nonconformityId),
    capaCaseId: asString(search.capaCaseId),
    meetingId: asString(search.meetingId),
    departmentId: asString(search.departmentId),
    assigneeUserId: asString(search.assigneeUserId),
    dueFrom: asString(search.dueFrom),
    dueTo: asString(search.dueTo),
    onlyOverdue: asBoolean(search.onlyOverdue),
    onlyAwaitingMyVerification: asBoolean(search.onlyAwaitingMyVerification),
    search: asString(search.search),
    sortBy: asString(search.sortBy),
    sortDir: search.sortDir === 'desc' ? 'desc' : 'asc',
  }),
  component: () => (
    <ProtectedRoute>
      <CapaActionsPage />
    </ProtectedRoute>
  ),
});

const capaActionDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/acoes-corretivas/$actionId',
  component: () => (
    <ProtectedRoute>
      <CapaActionDetailPage />
    </ProtectedRoute>
  ),
});

const circularsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/circulares',
  validateSearch: (search: Record<string, unknown>): CircularSearch => ({
    page: toPositiveInt(search.page) ?? 1,
    pageSize: toPositiveInt(search.pageSize),
    status: asStringArray(search.status),
    scope: asString(search.scope),
    search: asString(search.search),
    publishedFrom: asString(search.publishedFrom),
    publishedTo: asString(search.publishedTo),
    onlyPendingMyAcknowledgement: asBoolean(search.onlyPendingMyAcknowledgement),
    sortBy: asString(search.sortBy),
    sortDir: search.sortDir === 'asc' ? 'asc' : 'desc',
  }),
  component: () => (
    <ProtectedRoute>
      <CircularsPage />
    </ProtectedRoute>
  ),
});

const newCircularRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/circulares/nova',
  component: () => (
    <ProtectedRoute permissions={[Permission.CIRCULAR_MANAGE]}>
      <CircularFormPage />
    </ProtectedRoute>
  ),
});

const circularDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/circulares/$circularId',
  component: () => (
    <ProtectedRoute>
      <CircularDetailPage />
    </ProtectedRoute>
  ),
});

/**
 * Indicadores do SGQ.
 *
 * Exige UMA das permissões de quem faz gestão da qualidade — não é tela de
 * consulta geral: é o insumo da análise crítica pela direção. `mode="any"` é o
 * padrão do `<ProtectedRoute>`; o servidor recusa de novo, porque esconder rota
 * não é controle de acesso.
 */
const sgqIndicatorsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/indicadores-sgq',
  validateSearch: (search: Record<string, unknown>): SgqIndicatorsSearch => ({
    from: asString(search.from),
    to: asString(search.to),
    departmentId: asString(search.departmentId),
    source: asStringArray(search.source),
    type: asStringArray(search.type),
    bucket: asString(search.bucket),
    recurrenceWindowDays: toPositiveInt(search.recurrenceWindowDays),
  }),
  component: () => (
    <ProtectedRoute
      permissions={[
        Permission.NC_EVALUATE,
        Permission.NC_CLOSE,
        Permission.MEETING_MANAGE,
        Permission.CIRCULAR_MANAGE,
        Permission.AUDIT_READ,
      ]}
    >
      <SgqIndicatorsPage />
    </ProtectedRoute>
  ),
});

// ── Auditoria e administração ────────────────────────────────────────────────

const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/auditoria',
  validateSearch: (search: Record<string, unknown>): AuditSearch => ({
    page: toPositiveInt(search.page) ?? 1,
    pageSize: toPositiveInt(search.pageSize),
    entityType: asString(search.entityType),
    entityId: asString(search.entityId),
    actorUserId: asString(search.actorUserId),
    action: asStringArray(search.action),
    occurredFrom: asDate(search.occurredFrom),
    occurredTo: asDate(search.occurredTo),
    onlySigned: asBoolean(search.onlySigned) ?? false,
  }),
  component: () => (
    <ProtectedRoute permissions={[Permission.AUDIT_READ]}>
      <AuditTrailPage />
    </ProtectedRoute>
  ),
});

const organizationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/organizacao',
  component: () => (
    <ProtectedRoute permissions={[Permission.ORG_MANAGE]}>
      <OrganizationPage />
    </ProtectedRoute>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/usuarios',
  component: () => (
    <ProtectedRoute permissions={[Permission.USER_MANAGE, Permission.DEPARTMENT_MANAGE]}>
      <UsersPage />
    </ProtectedRoute>
  ),
});

const notificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/notificacoes',
  component: () => (
    <ProtectedRoute>
      <NotificationsPage />
    </ProtectedRoute>
  ),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  shellRoute.addChildren([
    dashboardRoute,
    changePasswordRoute,
    profileRoute,
    masterListRoute,
    newDocumentRoute,
    documentAdminRoute,
    documentDetailRoute,
    newVersionRoute,
    approvalsRoute,
    documentTypesRoute,
  meetingTypesRoute,
  requestTypesRoute,
    // SGQ — o segmento literal antes do parâmetro, como em `/documentos/novo`.
    nonconformitiesRoute,
    newNonconformityRoute,
    nonconformityDetailRoute,
    meetingsRoute,
    newMeetingRoute,
    meetingDetailRoute,
    capaActionsRoute,
    capaActionDetailRoute,
    circularsRoute,
    newCircularRoute,
    circularDetailRoute,
    sgqIndicatorsRoute,
    auditRoute,
    organizationRoute,
    usersRoute,
    notificationsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
  /*
   * O router anuncia a mudança de rota numa região `aria-live` própria. Numa
   * SPA não há recarregamento de página, então sem esse anúncio quem usa leitor
   * de tela navega sem saber que chegou em outro lugar.
   */
  defaultPendingMinMs: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
